import { GridRowId, useGridApiContext } from '@mui/x-data-grid-premium';
import { RiAlertFill, RiArrowDownSLine, RiCheckLine } from '@remixicon/react';
import { QboAccountMetadata } from '@shared/mappers/ThirdPartyEntities';
import { useMemo, useState } from 'react';
import ConfirmModal from 'src/components/containers/modals/ConfirmModal';
import { GridColObj } from 'src/modules/grids';
import { LedgerAccountDetail } from 'types/graphql';
import { useLinkQuickbooksAccount } from './quickbooks.api';

export type Row = {
  id: number;
  entityLink: { id: number };
  quickBooks: QboAccountMetadata;
  agriful: { id: number; path: string | null | undefined; number: string | null | undefined; name: string } | null;
};

export default function useQBOAccountMappingColumns(): GridColObj<Row> {
  return useMemo(
    () => ({
      quickBooks: {
        headerName: 'QBO Account (# and name)',
        minWidth: 220,
        flex: 1,
        type: 'string',
        valueGetter: (_value, row: Row) => {
          const number = row?.quickBooks?.accountNumber;
          const name = row?.quickBooks?.name;
          return [number, name].filter(Boolean).join(' ') || '--';
        },
      },
      agriful: {
        headerName: 'Agriful Account (# and name)',
        minWidth: 220,
        flex: 1,
        type: 'string',
        valueGetter: (_value, row: Row) => {
          if (!row?.agriful) return '';
          return [row.agriful.number, row.agriful.name].filter(Boolean).join(' ');
        },
        renderCell: (params) => {
          if (!params.row?.agriful) {
            return (
              <span className="flex items-center gap-1.5 text-orange-status">
                <RiAlertFill size={16} className="text-orange-icon" />
                Select mapping account
              </span>
            );
          }
          return params.value;
        },
      },
      accountType: {
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        type: 'string',
        valueGetter: (_value, row: Row) => row?.quickBooks?.classification ?? '',
        renderCell: (params) => <AccountTypeCell id={params.id} row={params.row} />,
      },
    }),
    []
  );
}

const AccountTypeCell = ({
  id,
  row,
}: {
  id: GridRowId;
  row: { quickBooks?: QboAccountMetadata; agriful?: { id: number } | null };
}) => {
  const apiRef = useGridApiContext();
  const classification = row?.quickBooks?.classification;
  const isLinked = !!row?.agriful;

  return (
    <button
      type="button"
      onClick={() => apiRef.current.toggleDetailPanel(id)}
      className="flex w-full items-center justify-end gap-2 cursor-pointer"
    >
      {isLinked && classification && <span className="badge badge-blue">{classification}</span>}
      <RiArrowDownSLine size={16} className="text-neutral-alpha-9" />
    </button>
  );
};

export function AccountLinkDetailPanel({
  row,
  availableAccounts,
  existingAgrifulLinks,
  onLinked,
}: {
  row: Row;
  availableAccounts: LedgerAccountDetail[];
  existingAgrifulLinks: Map<number, string>;
  onLinked: () => void;
}) {
  const apiRef = useGridApiContext();
  const [linkQuickbooksAccount, { loading }] = useLinkQuickbooksAccount();
  const [pendingOverride, setPendingOverride] = useState<{
    account: LedgerAccountDetail;
    existingQboLabel: string;
  } | null>(null);
  const options = validLinkingTargets(row.quickBooks, availableAccounts) ?? [];
  const currentPath = row?.agriful?.path;
  const currentAgrifulId = row?.agriful?.id;

  const performLink = async (account: LedgerAccountDetail) => {
    try {
      await linkQuickbooksAccount({ variables: { thirdPartyId: row.quickBooks.id, ledgerAccountId: account.id } });
      onLinked();
      apiRef.current.toggleDetailPanel(row.id);
    } catch {
      // mutation error is surfaced via snackbar in the hook
    }
  };

  const handleSelect = (account: LedgerAccountDetail) => {
    const existingQboLabel = existingAgrifulLinks.get(account.id);
    // If this Agriful account is already linked to a different QBO account,
    // ask the user to confirm overriding.
    if (existingQboLabel && account.id !== currentAgrifulId) {
      setPendingOverride({ account, existingQboLabel });
      return;
    }
    void performLink(account);
  };

  const handleConfirmOverride = async () => {
    if (!pendingOverride) return;
    const { account } = pendingOverride;
    setPendingOverride(null);
    await performLink(account);
  };

  return (
    <div className="flex justify-end border-y border-neutral-5 bg-neutral-2">
      <div className="w-[380px] max-h-[320px] overflow-auto">
        {options.length === 0 && (
          <div className="px-4 py-3 text-14 text-neutral-alpha-9">No compatible Agriful accounts available.</div>
        )}
        {options.map((account) => {
          const selected = account.fragmentAccountPath === currentPath;
          return (
            <button
              key={account.fragmentAccountPath}
              type="button"
              disabled={loading}
              onClick={() => handleSelect(account)}
              className="flex w-full items-center justify-between px-4 py-2 text-left text-14 hover:bg-neutral-4 disabled:opacity-60"
            >
              <span>
                {account.accountNumber} {account.accountName}
              </span>
              {selected && <RiCheckLine size={16} className="text-brand-text" />}
            </button>
          );
        })}
      </div>
      <ConfirmModal
        open={!!pendingOverride}
        title="Account already linked"
        prompt={
          pendingOverride
            ? `${pendingOverride.account.accountNumber} ${pendingOverride.account.accountName} is already linked to ${pendingOverride.existingQboLabel}. Applying it here will unset that previous link. Continue?`
            : ''
        }
        action="Override"
        destructive={false}
        disableConfirm={loading}
        handleClose={() => setPendingOverride(null)}
        handleConfirm={handleConfirmOverride}
      />
    </div>
  );
}

const validLinkingTargets = (metadata: QboAccountMetadata, accounts: LedgerAccountDetail[]) => {
  if (!metadata || !accounts) return [];
  if (metadata.classification === 'Asset') {
    return accounts.filter((a) =>
      ['CashAssets', 'CurrentAssets', 'LongTermAssets', 'OtherAssets', 'asset'].includes(a.accountType)
    );
  }

  if (metadata.classification === 'Liability') {
    return accounts.filter((a) => ['CurrentLiabilities', 'LongTermLiabilities', 'liability'].includes(a.accountType));
  }

  if (metadata.classification === 'Revenue') {
    return accounts.filter((a) => ['Income', 'OtherIncome', 'income', 'CurrentAssets'].includes(a.accountType));
  }

  if (metadata.classification === 'Expense') {
    return accounts.filter((a) =>
      ['DirectCosts', 'IndirectCosts', 'OtherCosts', 'expense', 'CurrentLiabilities'].includes(a.accountType)
    );
  }

  if (metadata.classification === 'Equity') {
    return accounts.filter((a) =>
      ['Equity', 'CurrentLiabilities', 'LongTermLiabilities', 'liability'].includes(a.accountType)
    );
  }
  return [];
};
